import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import './App.css';
import UserDetailsPanel from './UserDetailsPanel';
import './UserDetailsPanel.css';
import PersonalDashboard from './PersonalDashboard';
import './PersonalDashboard.css';
import SavedMessages from './SavedMessages';
import './SavedMessages.css';
import MentorAuth from './MentorAuth';
import './MentorAuth.css';

// Dynamic URLs based on environment - auto-detect production
const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const API_BASE_URL = isProduction
  ? `${window.location.protocol}//${window.location.host}`
  : 'http://localhost:5000';
const SOCKET_URL = API_BASE_URL;

// Export for other components
export { API_BASE_URL, SOCKET_URL, isProduction };

const socket = io(SOCKET_URL, {
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionAttempts: 5
});

// שלבי ההתקדמות
const progressStages = {
  'watching_training': { label: 'צפייה בהכשרה', icon: '📺', color: '#4A90E2' },
  'back_test': { label: 'בק טסט', icon: '📊', color: '#7B68EE' },
  'forward': { label: 'פורוורד', icon: '⏩', color: '#50C878' },
  'live_trading': { label: 'מסחר בפועל', icon: '💹', color: '#00A884' },
  'completed': { label: 'סיים', icon: '✅', color: '#34C759' }
};

function App() {
  // מצב התחברות מנטור
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentMentorId, setCurrentMentorId] = useState(null);

  const [isReady, setIsReady] = useState(false);
  const [chats, setChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTimer, setRecordingTimer] = useState(0);
    const [selectedUser, setSelectedUser] = useState(null);
  const [showUserPanel, setShowUserPanel] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showChatInfo, setShowChatInfo] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [showSearchInChat, setShowSearchInChat] = useState(false);
  const [searchInChatText, setSearchInChatText] = useState('');
  const [playingAudio, setPlayingAudio] = useState(null);
  const [showPersonalDashboard, setShowPersonalDashboard] = useState(false);
  const [showSavedMessages, setShowSavedMessages] = useState(false);
  const [savedMessages, setSavedMessages] = useState([]);
  const [showMobileUserModal, setShowMobileUserModal] = useState(false);
  const [mobileModalTab, setMobileModalTab] = useState('info'); // info, progress, actions, journals
  const [userJournals, setUserJournals] = useState([]);
  const [userTrades, setUserTrades] = useState([]);
  const [mobileUserData, setMobileUserData] = useState(null); // נתוני משתמש למובייל
  const [showMessageMenu, setShowMessageMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedMessageForMenu, setSelectedMessageForMenu] = useState(null);
  const [starredMessages, setStarredMessages] = useState([]);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const audioRef = useRef(null);
  const longPressTimerRef = useRef(null);

  // Emojis פופולריים
  const popularEmojis = ['😀', '😂', '🥰', '😎', '😍', '🤔', '😅', '😊', '👍', '❤️', '🙏', '😭', '😘', '🤗', '🎉', '🔥', '💯', '👏', '😉', '🤷'];

  // Load user data and journals when modal opens
  useEffect(() => {
    if (showMobileUserModal && selectedChat && selectedChat.inFirebase === 1) {
      const phone = selectedChat.id.split('@')[0];

      // 1. טעינת נתוני המשתמש
      socket.emit('get_user_by_phone', phone);

      const handleUserData = (data) => {
        console.log('📥 Mobile: קיבלתי נתוני משתמש:', data);
        if (data.user) {
          setMobileUserData(data.user);

          // 2. אחרי שקיבלנו את נתוני המשתמש, נטען את היומנים
          if (data.user.userId) {
            console.log(`🔍 Mobile: מבקש יומני מסחר עבור משתמש: ${data.user.userId}`);
            socket.emit('get_user_journals', { userId: data.user.userId });
          }
        } else {
          setMobileUserData(null);
        }
      };

      const handleJournalsData = (data) => {
        console.log('📥 Mobile: קיבלתי יומני מסחר:', data);
        if (data.success) {
          setUserJournals(data.journals || []);
        } else {
          console.error('❌ Mobile: שגיאה בטעינת יומני מסחר:', data.error);
          setUserJournals([]);
        }
      };

      socket.on('user_data', handleUserData);
      socket.on('user_journals_data', handleJournalsData);

      return () => {
        socket.off('user_data', handleUserData);
        socket.off('user_journals_data', handleJournalsData);
      };
    }
  }, [showMobileUserModal, selectedChat]);

  // 🔄 Auto-retry mechanism for loading chats
  useEffect(() => {
    if (!isReady || chats.length > 0 || !chatsLoading) return;

    const retryInterval = setInterval(() => {
      console.log('🔄 Retrying to load chats...');
      socket.emit('get_chats');
    }, 3000); // כל 3 שניות

    return () => clearInterval(retryInterval);
  }, [isReady, chats.length, chatsLoading]);

  useEffect(() => {
    // Socket listeners - רק אחרי authentication מוצלח
    socket.on('ready', () => {
      setIsReady(true);
      console.log('📱 WhatsApp ready - requesting chats...');
      setChatsLoading(true);
      socket.emit('get_chats');
    });

    socket.on('chats', (chatsData) => {
      setChats(chatsData);
      // עדכון המשתמש הנבחר אם יש עדכון
      if (selectedUser && chatsData) {
        const updatedChat = chatsData.find(chat => chat.id === selectedUser.id);
        if (updatedChat) {
          setSelectedUser({
            ...selectedUser,
            tags: updatedChat.tags || '',
            progressStage: updatedChat.progressStage || 'watching_training'
          });
        }
      }
    });

    socket.on('chats_loaded', (chatsData) => {
      console.log('✅ Chats loaded successfully:', chatsData?.length || 0, 'chats');
      setChats(chatsData || []);
      setChatsLoading(false);
    });

    socket.on('chats_updated', (chatsData) => {
      console.log('🔄 Chats updated:', chatsData?.length || 0, 'chats');
      setChats(chatsData || []);
      setChatsLoading(false);
      // עדכון המשתמש הנבחר אם יש עדכון
      if (selectedUser && chatsData) {
        const updatedChat = chatsData.find(chat => chat.id === selectedUser.id);
        if (updatedChat) {
          setSelectedUser({
            ...selectedUser,
            tags: updatedChat.tags || '',
            progressStage: updatedChat.progressStage || 'watching_training'
          });
        }
      }
    });

    socket.on('messages', (data) => {
      console.log('📩 Received messages:', data);
      setMessages(data.messages || []);
    });

    socket.on('message', (message) => {
      if (selectedChat && message.chatId === selectedChat.id) {
        setMessages((prev) => [...prev, message]);
        // Play notification sound
        playNotificationSound();
      }
      socket.emit('get_chats');
    });

    socket.on('message_sent', () => {
      setMessageText('');
      setReplyToMessage(null);
    });

    socket.on('error', (error) => {
      alert(error);
    });

    socket.on('disconnected', () => {
      setIsReady(false);
    });

    return () => {
      socket.off('ready');
      socket.off('chats');
      socket.off('messages');
      socket.off('message');
      socket.off('message_sent');
      socket.off('error');
      socket.off('disconnected');
    };
  }, [selectedChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Play notification sound
  const playNotificationSound = () => {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQ==');
    audio.volume = 0.5;
    audio.play().catch(e => console.log('Could not play sound'));
  };

const handleChatClick = (chat) => {
  console.log('💬 Chat clicked:', chat.id, chat.name);
  setSelectedChat(chat);
  setShowMobileChat(true);
  setMessages([]); // נקה להצגת loading
  console.log('📤 Requesting messages for:', chat.id);
  socket.emit('get_messages', chat.id);
  setSelectedMessages([]);
  setIsSelectionMode(false);
  setShowSearchInChat(false);
  setSearchInChatText('');

  // העברת כל המידע של הצ'אט לפאנל המשתמש
  setSelectedUser({
    id: chat.id,
    name: chat.name,
    email: chat.email || '',
    phone: chat.phoneNumber || chat.phone || '',
    tags: chat.tags || '',
    progressStage: chat.progressStage || 'watching_training'
  });
  setShowUserPanel(true);
};
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedChat) return;
    
    const messageData = {
      chatId: selectedChat.id,
      body: messageText
    };
    
    if (replyToMessage) {
      messageData.quotedMessageId = replyToMessage.id;
    }
    
    socket.emit('send_message', messageData);
    
    // Add typing indicator
    clearTimeout(typingTimeoutRef.current);
    setIsTyping(false);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedChat) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      socket.emit('send_message', {
        chatId: selectedChat.id,
        body: messageText || '',
        media: { 
          mimetype: file.type, 
          data: base64, 
          filename: file.name 
        }
      });
      setMessageText('');
    };
    reader.readAsDataURL(file);
    setShowAttachMenu(false);
  };

  // Enhanced recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: 'audio/ogg; codecs=opus' 
        });
        
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          socket.emit('send_message', {
            chatId: selectedChat.id,
            body: '',
            media: { 
              mimetype: 'audio/ogg', 
              data: base64, 
              filename: 'voice.ogg' 
            }
          });
        };
        reader.readAsDataURL(audioBlob);
        
        stream.getTracks().forEach(track => track.stop());
        setRecordingTimer(0);
      };
      
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      
      // Start timer
      let seconds = 0;
      recordingIntervalRef.current = setInterval(() => {
        seconds++;
        setRecordingTimer(seconds);
      }, 1000);
      
    } catch (err) {
      console.error('Error accessing microphone:', err);
      alert('לא ניתן לגשת למיקרופון. אנא בדוק את ההרשאות.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
      setRecordingTimer(0);
      audioChunksRef.current = [];
    }
  };

  const handleTyping = (e) => {
    setMessageText(e.target.value);
    
    // Simulate typing indicator
    if (!isTyping) {
      setIsTyping(true);
    }
    
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 1000);
  };

  const handleEmojiClick = (emoji) => {
    setMessageText(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleImageClick = (imagePath) => {
    setLightboxImage(imagePath);
    setShowLightbox(true);
  };

  const handleMessageSelect = (messageId) => {
    if (selectedMessages.includes(messageId)) {
      setSelectedMessages(prev => prev.filter(id => id !== messageId));
    } else {
      setSelectedMessages(prev => [...prev, messageId]);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedMessages.length > 0 && window.confirm('למחוק הודעות שנבחרו?')) {
      socket.emit('delete_messages', selectedMessages);
      setSelectedMessages([]);
      setIsSelectionMode(false);
    }
  };

  const handleForwardSelected = () => {
    // Implementation for forward messages
    console.log('Forward messages:', selectedMessages);
  };

  const handleReply = (message) => {
    setReplyToMessage(message);
    setShowMessageMenu(false);
  };

  const handleMessageRightClick = (e, message) => {
    e.preventDefault();
    setMenuPosition({ x: e.clientX, y: e.clientY });
    setSelectedMessageForMenu(message);
    setShowMessageMenu(true);
  };

  const handleStarMessage = (messageId) => {
    setStarredMessages(prev => {
      if (prev.includes(messageId)) {
        return prev.filter(id => id !== messageId);
      } else {
        return [...prev, messageId];
      }
    });
    setShowMessageMenu(false);
  };

  const handleDeleteMessage = (messageId) => {
    if (window.confirm('למחוק הודעה זו?')) {
      socket.emit('delete_messages', [messageId]);
      setShowMessageMenu(false);
    }
  };

  const handleForwardMessage = (messageId) => {
    console.log('Forward message:', messageId);
    setShowMessageMenu(false);
    // TODO: Implement forward message
  };

  const handleLongPressStart = (message) => {
    longPressTimerRef.current = setTimeout(() => {
      setIsSelectionMode(true);
      handleMessageSelect(message.id);
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
  };

  const handleAudioPlay = (messageId, audioPath) => {
    if (playingAudio === messageId) {
      // עצור את הנגינה
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudio(null);
    } else {
      // התחל לנגן
      if (audioRef.current) {
        audioRef.current.pause();
      }

      const audio = new Audio(`${API_BASE_URL}/${audioPath}`);
      audioRef.current = audio;

      audio.play();
      setPlayingAudio(messageId);

      audio.onended = () => {
        setPlayingAudio(null);
        audioRef.current = null;
      };
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return 'היום';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'אתמול';
    return date.toLocaleDateString('he-IL');
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle mentor authentication success
  const handleAuthSuccess = (mentorId) => {
    setCurrentMentorId(mentorId);
    setIsAuthenticated(true);
  };

  // Show authentication screen if not authenticated
  console.log('🔐 MENTOR AUTH CHECK - isAuthenticated:', isAuthenticated);

  if (!isAuthenticated) {
    console.log('📱 SHOWING MENTOR AUTH SCREEN');
    return <MentorAuth socket={socket} onAuthSuccess={handleAuthSuccess} />;
  }

  console.log('✅ USER AUTHENTICATED - SHOWING MAIN APP');

  // Filter chats and messages (only after authentication)
  const filteredChats = chats.filter(chat => {
    const matchesSearch = chat.name?.toLowerCase().includes(searchText.toLowerCase()) || false;

    // תמיד הסתר קבוצות מרשימת הצ'אטים - רק אנשים פרטיים
    if (chat.isGroup === 1) return false;

    if (filterType === 'all') return matchesSearch;
    if (filterType === 'contacts') return matchesSearch && chat.isGroup === 0;
    if (filterType === 'groups') return matchesSearch && chat.isGroup === 1;
    return matchesSearch;
  });

  const filteredMessages = messages.filter(msg => {
    if (!searchInChatText) return true;
    return msg.body?.toLowerCase().includes(searchInChatText.toLowerCase());
  });

  // Main app
  return (
    <div className="app">
      {/* Message Context Menu */}
      {showMessageMenu && selectedMessageForMenu && (
        <>
          <div
            className="context-menu-overlay"
            onClick={() => setShowMessageMenu(false)}
          />
          <div
            className="message-context-menu"
            style={{
              top: `${menuPosition.y}px`,
              left: `${menuPosition.x}px`
            }}
          >
            <button onClick={() => handleReply(selectedMessageForMenu)}>
              <svg viewBox="0 0 24 24" height="20" width="20">
                <path fill="currentColor" d="M10 12H20V14H10V18L4 12L10 6V10Z"/>
              </svg>
              <span>השב</span>
            </button>
            <button onClick={() => handleStarMessage(selectedMessageForMenu.id)}>
              <svg viewBox="0 0 24 24" height="20" width="20">
                <path fill="currentColor" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
              </svg>
              <span>{starredMessages.includes(selectedMessageForMenu?.id) ? 'בטל סימון בכוכב' : 'סמן בכוכב'}</span>
            </button>
            <button onClick={() => handleForwardMessage(selectedMessageForMenu.id)}>
              <svg viewBox="0 0 24 24" height="20" width="20">
                <path fill="currentColor" d="M14.2 9c-.4-.4-1.1-.4-1.6 0l-1.4 1.4c-.1.1-.2.2-.4.2H5c-.6 0-1 .4-1 1s.4 1 1 1h5.9c.1 0 .3.1.4.2l1.4 1.4c.2.2.5.3.8.3s.6-.1.8-.3c.4-.4.4-1.1 0-1.6L12.8 11l1.4-1.4c.4-.5.4-1.2 0-1.6z"/>
              </svg>
              <span>העבר הודעה</span>
            </button>
            <button
              onClick={() => {
                setIsSelectionMode(true);
                handleMessageSelect(selectedMessageForMenu.id);
                setShowMessageMenu(false);
              }}
            >
              <svg viewBox="0 0 24 24" height="20" width="20">
                <path fill="currentColor" d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
              </svg>
              <span>בחר הודעות</span>
            </button>
            <button onClick={() => handleDeleteMessage(selectedMessageForMenu.id)} className="delete-option">
              <svg viewBox="0 0 24 24" height="20" width="20">
                <path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
              <span>מחק הודעה</span>
            </button>
          </div>
        </>
      )}

      {/* Lightbox */}
      {showLightbox && (
        <div className="lightbox" onClick={() => setShowLightbox(false)}>
          <button className="lightbox-close" onClick={() => setShowLightbox(false)}>
            <svg viewBox="0 0 24 24" height="24" width="24">
              <path fill="currentColor" d="M19.6 17.8L13.3 11.5l6.3-6.3c.5-.5.5-1.3 0-1.8-.5-.5-1.3-.5-1.8 0l-6.3 6.3-6.3-6.3c-.5-.5-1.3-.5-1.8 0-.5.5-.5 1.3 0 1.8l6.3 6.3-6.3 6.3c-.5.5-.5 1.3 0 1.8.3.3.6.4.9.4s.6-.1.9-.4l6.3-6.3 6.3 6.3c.3.3.6.4.9.4s.6-.1.9-.4c.5-.5.5-1.3 0-1.8z"></path>
            </svg>
          </button>
          <img src={`${API_BASE_URL}/${lightboxImage}`} alt="תמונה" onClick={(e) => e.stopPropagation()} />
          <div className="lightbox-toolbar">
            <button title="הורד">
              <svg viewBox="0 0 24 24" height="24" width="24">
                <path fill="currentColor" d="M18.9 9.5c-.1 0-.2 0-.3.1L13 15.1V3c0-.6-.4-1-1-1s-1 .4-1 1v12.1L5.4 9.6c-.4-.4-1-.4-1.4 0-.4.4-.4 1 0 1.4l7.3 7.3c.2.2.4.3.7.3.3 0 .5-.1.7-.3L20 11c.4-.4.4-1 0-1.4-.2-.3-.6-.4-.9-.4zM21 16c-.6 0-1 .4-1 1v3H4v-3c0-.6-.4-1-1-1s-1 .4-1 1v4c0 .6.4 1 1 1h18c.6 0 1-.4 1-1v-4c0-.6-.4-1-1-1z"></path>
              </svg>
            </button>
            <button title="שתף">
              <svg viewBox="0 0 24 24" height="24" width="24">
                <path fill="currentColor" d="M12 2.6l-.9.9L4.4 10v9c0 1.2.9 2.1 2.1 2.1h3.1v-7.5h5v7.5h3.1c1.2 0 2.1-.9 2.1-2.1v-9l-6.6-6.5-.9-.9-.3.3z"></path>
              </svg>
            </button>
            <button title="העבר">
              <svg viewBox="0 0 24 24" height="24" width="24">
                <path fill="currentColor" d="M14.2 9c-.4-.4-1.1-.4-1.6 0l-1.4 1.4c-.1.1-.2.2-.4.2H5c-.6 0-1 .4-1 1s.4 1 1 1h5.9c.1 0 .3.1.4.2l1.4 1.4c.2.2.5.3.8.3s.6-.1.8-.3c.4-.4.4-1.1 0-1.6L12.8 11l1.4-1.4c.4-.5.4-1.2 0-1.6zM19 8h-3.4c-.3 0-.6.3-.6.6s.3.6.6.6H19c.3 0 .5.2.5.5v8.6c0 .3-.2.5-.5.5h-3.4c-.3 0-.6.3-.6.6s.3.6.6.6H19c.8 0 1.5-.7 1.5-1.5V9.5C20.5 8.7 19.8 8 19 8z"></path>
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className={`sidebar ${!showMobileChat ? 'mobile-show' : 'mobile-hide'}`}>
        <div className="sidebar-header">
          <div className="user-avatar" onClick={() => setShowPersonalDashboard(true)} title="האזור האישי שלי" style={{ cursor: 'pointer' }}>
            <img src="/logoo.png" alt="Logo" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
          </div>
          <div className="header-icons">
            <button className="icon-btn" title="הודעות שמורות" onClick={() => setShowSavedMessages(true)}>
              <svg viewBox="0 0 24 24" height="24" width="24">
                <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-2-2H7v-1h10v1zm0-2H7v-1h10v1zm0-2H7V9h10v4z"/>
              </svg>
            </button>
            <button className="icon-btn" title="צ'אט חדש">
              <svg viewBox="0 0 24 24" height="24" width="24">
                <path fill="currentColor" d="M19.005 3.175H4.674C3.642 3.175 3 3.789 3 4.821V21.02l3.544-3.514h12.461c1.033 0 2.064-1.06 2.064-2.093V4.821c-.001-1.032-1.032-1.646-2.064-1.646zm-4.989 9.869H7.041V11.1h6.975v1.944zm3-4H7.041V7.1h9.975v1.944z"></path>
              </svg>
            </button>
            <div className="dropdown">
              <button className="icon-btn" title="תפריט" onClick={() => setShowMenu(!showMenu)}>
                <svg viewBox="0 0 24 24" height="24" width="24">
                  <path fill="currentColor" d="M12 7a2 2 0 1 0-.001-4.001A2 2 0 0 0 12 7zm0 2a2 2 0 1 0-.001 3.999A2 2 0 0 0 12 9zm0 6a2 2 0 1 0-.001 3.999A2 2 0 0 0 12 15z"></path>
                </svg>
              </button>
              {showMenu && (
                <div className="dropdown-menu">
                  <button>קבוצה חדשה</button>
                  <button>הודעות מסומנות בכוכב</button>
                  <button>הגדרות</button>
                  <button>התנתק</button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="search-wrapper">
          <div className="search-box">
            <svg viewBox="0 0 24 24" height="24" width="24">
              <path fill="currentColor" d="M15.009 13.805h-.636l-.22-.219a5.184 5.184 0 0 0 1.256-3.386 5.207 5.207 0 1 0-5.207 5.208 5.183 5.183 0 0 0 3.385-1.255l.221.22v.635l4.004 3.999 1.194-1.195-3.997-4.007zm-4.808 0a3.605 3.605 0 1 1 0-7.21 3.605 3.605 0 0 1 0 7.21z"></path>
            </svg>
            <input
              type="text"
              placeholder="חפש או התחל שיחה חדשה"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>

        <div className="chats-list">
          {chatsLoading && chats.length === 0 ? (
            <div className="chats-loading">
              <div className="loading-spinner"></div>
              <p>טוען אנשי קשר...</p>
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="no-chats">
              <svg viewBox="0 0 212 212" height="80" width="80">
                <path fill="#8696a0" d="M106.251.5C164.653.5 212 47.846 212 106.25S164.653 212 106.25 212C47.846 212 .5 164.654.5 106.25S47.846.5 106.251.5z"></path>
              </svg>
              <h3>אין שיחות</h3>
              <p>התחל שיחה חדשה על ידי לחיצה על אייקון הודעה חדשה</p>
            </div>
          ) : (
            filteredChats.map((chat) => (
            <div
              key={chat.id}
              className={`chat-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
              onClick={() => handleChatClick(chat)}
            >
              <div className="chat-avatar">
                {chat.profilePic ? (
                  <img src={chat.profilePic} alt={chat.name} />
                ) : (
                  <svg viewBox="0 0 212 212" height="49" width="49">
                    <path fill="#6a7175" d="M106.251.5C164.653.5 212 47.846 212 106.25S164.653 212 106.25 212C47.846 212 .5 164.654.5 106.25S47.846.5 106.251.5z"></path>
                    <path fill="#111b21" d="M173.561 171.615a62.767 62.767 0 0 0-2.065-2.955 67.7 67.7 0 0 0-2.608-3.299 70.112 70.112 0 0 0-3.184-3.527 71.097 71.097 0 0 0-5.924-5.47 72.458 72.458 0 0 0-10.204-7.026 75.2 75.2 0 0 0-5.98-3.055c-.062-.028-.118-.059-.18-.087-9.792-4.44-22.106-7.529-37.416-7.529s-27.624 3.089-37.416 7.529c-.338.153-.653.318-.985.474a75.37 75.37 0 0 0-6.229 3.298 72.589 72.589 0 0 0-9.15 6.395 71.243 71.243 0 0 0-5.924 5.47 70.064 70.064 0 0 0-3.184 3.527 67.142 67.142 0 0 0-2.609 3.299 63.292 63.292 0 0 0-2.065 2.955 56.33 56.33 0 0 0-1.447 2.324c-.033.056-.073.119-.104.174a47.92 47.92 0 0 0-1.07 1.926c-.559 1.068-.818 1.678-.818 1.678v.398c18.285 17.927 43.322 28.985 70.945 28.985 27.678 0 52.761-11.103 71.055-29.095v-.289s-.619-1.45-1.992-3.778a58.346 58.346 0 0 0-1.446-2.322zM106.002 125.5c2.645 0 5.212-.253 7.68-.737a38.272 38.272 0 0 0 3.624-.896 37.124 37.124 0 0 0 5.12-1.958 36.307 36.307 0 0 0 6.15-3.67 35.923 35.923 0 0 0 9.489-10.48 36.558 36.558 0 0 0 2.422-4.84 37.051 37.051 0 0 0 1.716-5.25c.299-1.208.542-2.443.725-3.701.275-1.887.417-3.827.417-5.811s-.142-3.925-.417-5.811a38.734 38.734 0 0 0-1.215-5.494 36.68 36.68 0 0 0-3.648-8.298 35.923 35.923 0 0 0-9.489-10.48 36.347 36.347 0 0 0-6.15-3.67 37.124 37.124 0 0 0-5.12-1.958 37.67 37.67 0 0 0-3.624-.896 39.875 39.875 0 0 0-7.68-.737c-21.162 0-37.345 16.183-37.345 37.345 0 21.159 16.183 37.342 37.345 37.342z"></path>
                  </svg>
                )}
              </div>
              <div className="chat-info">
                <div className="chat-header">
                  <h3>
                    {chat.name}
                    {/* תגית ליד השם - רק למי שקיים ב-Firebase (תלמידים בהכשרה) */}
                    {chat.inFirebase === 1 && (() => {
                      const stage = chat.progressStage || 'watching_training';
                      const stageData = progressStages[stage];
                      if (stageData) {
                        return (
                          <span className="inline-stage-tag" style={{ backgroundColor: stageData.color }}>
                            {stageData.icon} {stageData.label}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </h3>
                  <span className="chat-time">{formatDate(chat.timestamp)}</span>
                </div>
                <div className="chat-preview">
                  <p>{chat.lastMessage || 'אין הודעות'}</p>
                  {chat.unreadCount > 0 && (
                    <span className="unread-badge">{chat.unreadCount}</span>
                  )}
                </div>
              </div>
            </div>
          ))
          )}
        </div>
      </div>

      <div className={`chat-container ${showMobileChat ? 'mobile-show' : ''}`}>
        {selectedChat ? (
          <>
            <div className="chat-header">
              <button 
                className="mobile-back-btn"
                onClick={() => setShowMobileChat(false)}
              >
                <svg viewBox="0 0 24 24" height="24" width="24">
                  <path fill="currentColor" d="M12 4l1.4 1.4L7.8 11H20v2H7.8l5.6 5.6L12 20l-8-8 8-8z"></path>
                </svg>
              </button>
              <div className="chat-header-info" onClick={() => setShowChatInfo(true)}>
                <div className="chat-avatar">
                  {selectedChat.profilePic ? (
                    <img src={selectedChat.profilePic} alt={selectedChat.name} />
                  ) : (
                    <svg viewBox="0 0 212 212" height="40" width="40">
                      <path fill="#DFE5E7" d="M106.251.5C164.653.5 212 47.846 212 106.25S164.653 212 106.25 212C47.846 212 .5 164.654.5 106.25S47.846.5 106.251.5z"></path>
                      <path fill="#FFF" d="M173.561 171.615a62.767 62.767 0 0 0-2.065-2.955 67.7 67.7 0 0 0-2.608-3.299 70.112 70.112 0 0 0-3.184-3.527 71.097 71.097 0 0 0-5.924-5.47 72.458 72.458 0 0 0-10.204-7.026 75.2 75.2 0 0 0-5.98-3.055c-.062-.028-.118-.059-.18-.087-9.792-4.44-22.106-7.529-37.416-7.529s-27.624 3.089-37.416 7.529c-.338.153-.653.318-.985.474a75.37 75.37 0 0 0-6.229 3.298 72.589 72.589 0 0 0-9.15 6.395 71.243 71.243 0 0 0-5.924 5.47 70.064 70.064 0 0 0-3.184 3.527 67.142 67.142 0 0 0-2.609 3.299 63.292 63.292 0 0 0-2.065 2.955 56.33 56.33 0 0 0-1.447 2.324c-.033.056-.073.119-.104.174a47.92 47.92 0 0 0-1.07 1.926c-.559 1.068-.818 1.678-.818 1.678v.398c18.285 17.927 43.322 28.985 70.945 28.985 27.678 0 52.761-11.103 71.055-29.095v-.289s-.619-1.45-1.992-3.778a58.346 58.346 0 0 0-1.446-2.322zM106.002 125.5c2.645 0 5.212-.253 7.68-.737a38.272 38.272 0 0 0 3.624-.896 37.124 37.124 0 0 0 5.12-1.958 36.307 36.307 0 0 0 6.15-3.67 35.923 35.923 0 0 0 9.489-10.48 36.558 36.558 0 0 0 2.422-4.84 37.051 37.051 0 0 0 1.716-5.25c.299-1.208.542-2.443.725-3.701.275-1.887.417-3.827.417-5.811s-.142-3.925-.417-5.811a38.734 38.734 0 0 0-1.215-5.494 36.68 36.68 0 0 0-3.648-8.298 35.923 35.923 0 0 0-9.489-10.48 36.347 36.347 0 0 0-6.15-3.67 37.124 37.124 0 0 0-5.12-1.958 37.67 37.67 0 0 0-3.624-.896 39.875 39.875 0 0 0-7.68-.737c-21.162 0-37.345 16.183-37.345 37.345 0 21.159 16.183 37.342 37.345 37.342z"></path>
                    </svg>
                  )}
                </div>
                <div>
                  <h3>{selectedChat.name}</h3>
                  <p>{isTyping ? 'מקליד...' : 'לחץ כאן למידע על איש הקשר'}</p>
                </div>
              </div>
              <div className="header-icons">
                {/* כפתור פרטי משתמש - רק במובייל ורק למשתמשי Firebase */}
                {selectedChat && selectedChat.inFirebase === 1 && (
                  <button
                    className="icon-btn mobile-user-info-btn"
                    title="פרטי משתמש"
                    onClick={() => setShowMobileUserModal(true)}
                  >
                    <svg viewBox="0 0 24 24" height="24" width="24">
                      <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                    </svg>
                  </button>
                )}
                <button className="icon-btn" title="שיחת וידאו">
                  <svg viewBox="0 0 24 24" height="24" width="24">
                    <path fill="currentColor" d="M3.5 6.5C3.5 5.1 4.6 4 6 4h6c1.4 0 2.5 1.1 2.5 2.5v11c0 1.4-1.1 2.5-2.5 2.5H6c-1.4 0-2.5-1.1-2.5-2.5v-11zM15 9.5v5l5 2.5V7l-5 2.5z"></path>
                  </svg>
                </button>
                <button className="icon-btn" title="שיחת קול">
                  <svg viewBox="0 0 24 24" height="24" width="24">
                    <path fill="currentColor" d="M14.828 18.736l-1.415-1.414a7.979 7.979 0 0 0 0-10.646l1.415-1.414a9.96 9.96 0 0 1 0 13.474zm-2.828-2.829l-1.415-1.414a4.001 4.001 0 0 0 0-5.002l1.415-1.414a5.981 5.981 0 0 1 0 7.83z"></path>
                  </svg>
                </button>
                <button
                  className="icon-btn"
                  title="חיפוש"
                  onClick={() => setShowSearchInChat(!showSearchInChat)}
                >
                  <svg viewBox="0 0 24 24" height="24" width="24">
                    <path fill="currentColor" d="M15.9 14.3H15l-.3-.3c1-1.1 1.6-2.7 1.6-4.3 0-3.7-3-6.7-6.7-6.7S3 6 3 9.7s3 6.7 6.7 6.7c1.6 0 3.2-.6 4.3-1.6l.3.3v.8l5.1 5.1 1.5-1.5-5-5.2zm-6.2 0c-2.6 0-4.6-2.1-4.6-4.6s2.1-4.6 4.6-4.6 4.6 2.1 4.6 4.6-2 4.6-4.6 4.6z"></path>
                  </svg>
                </button>
                <button className="icon-btn" title="תפריט">
                  <svg viewBox="0 0 24 24" height="24" width="24">
                    <path fill="currentColor" d="M12 7a2 2 0 1 0-.001-4.001A2 2 0 0 0 12 7zm0 2a2 2 0 1 0-.001 3.999A2 2 0 0 0 12 9zm0 6a2 2 0 1 0-.001 3.999A2 2 0 0 0 12 15z"></path>
                  </svg>
                </button>
              </div>
            </div>

            {/* Search in chat bar */}
            {showSearchInChat && (
              <div className="search-in-chat-bar">
                <input
                  type="text"
                  placeholder="חיפוש בצ'אט..."
                  value={searchInChatText}
                  onChange={(e) => setSearchInChatText(e.target.value)}
                  autoFocus
                />
                <button 
                  className="icon-btn"
                  onClick={() => {
                    setShowSearchInChat(false);
                    setSearchInChatText('');
                  }}
                >
                  <svg viewBox="0 0 24 24" height="24" width="24">
                    <path fill="currentColor" d="M19.6 17.8L13.3 11.5l6.3-6.3c.5-.5.5-1.3 0-1.8-.5-.5-1.3-.5-1.8 0l-6.3 6.3-6.3-6.3c-.5-.5-1.3-.5-1.8 0-.5.5-.5 1.3 0 1.8l6.3 6.3-6.3 6.3c-.5.5-.5 1.3 0 1.8.3.3.6.4.9.4s.6-.1.9-.4l6.3-6.3 6.3 6.3c.3.3.6.4.9.4s.6-.1.9-.4c.5-.5.5-1.3 0-1.8z"></path>
                  </svg>
                </button>
              </div>
            )}

            {/* Selection mode toolbar */}
            {isSelectionMode && selectedMessages.length > 0 && (
              <div className="selection-toolbar">
                <button onClick={() => {
                  setIsSelectionMode(false);
                  setSelectedMessages([]);
                }}>
                  ביטול
                </button>
                <span>{selectedMessages.length} נבחרו</span>
                <div className="selection-actions">
                  <button onClick={handleDeleteSelected} title="מחק">
                    <svg viewBox="0 0 24 24" height="24" width="24">
                      <path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path>
                    </svg>
                  </button>
                  <button onClick={handleForwardSelected} title="העבר">
                    <svg viewBox="0 0 24 24" height="24" width="24">
                      <path fill="currentColor" d="M14.2 9c-.4-.4-1.1-.4-1.6 0l-1.4 1.4c-.1.1-.2.2-.4.2H5c-.6 0-1 .4-1 1s.4 1 1 1h5.9c.1 0 .3.1.4.2l1.4 1.4c.2.2.5.3.8.3s.6-.1.8-.3c.4-.4.4-1.1 0-1.6L12.8 11l1.4-1.4c.4-.5.4-1.2 0-1.6z"></path>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            <div className="messages-container">
              {/* Reply preview */}
              {replyToMessage && (
                <div className="reply-preview">
                  <div className="reply-content">
                    <span className="reply-name">
                      {replyToMessage.fromMe ? 'את/ה' : selectedChat.name}
                    </span>
                    <p className="reply-text">{replyToMessage.body}</p>
                  </div>
                  <button 
                    className="reply-close"
                    onClick={() => setReplyToMessage(null)}
                  >
                    <svg viewBox="0 0 24 24" height="24" width="24">
                      <path fill="currentColor" d="M19.6 17.8L13.3 11.5l6.3-6.3c.5-.5.5-1.3 0-1.8-.5-.5-1.3-.5-1.8 0l-6.3 6.3-6.3-6.3c-.5-.5-1.3-.5-1.8 0-.5.5-.5 1.3 0 1.8l6.3 6.3-6.3 6.3c-.5.5-.5 1.3 0 1.8.3.3.6.4.9.4s.6-.1.9-.4l6.3-6.3 6.3 6.3c.3.3.6.4.9.4s.6-.1.9-.4c.5-.5.5-1.3 0-1.8z"></path>
                    </svg>
                  </button>
                </div>
              )}

              {filteredMessages.map((message, index) => (
                <div
                  key={message.id || index}
                  className={`message ${message.fromMe ? 'sent' : 'received'} ${selectedMessages.includes(message.id) ? 'selected' : ''}`}
                  onContextMenu={(e) => handleMessageRightClick(e, message)}
                  onMouseDown={() => handleLongPressStart(message)}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  onTouchStart={() => handleLongPressStart(message)}
                  onTouchEnd={handleLongPressEnd}
                  onClick={() => {
                    if (isSelectionMode) {
                      handleMessageSelect(message.id);
                    }
                  }}
                >
                  <div className="message-content">
                    {message.quotedMessageId && (
                      <div className="quoted-message">
                        <div className="quoted-bar"></div>
                        <div className="quoted-content">
                          <p>הודעה מצוטטת</p>
                        </div>
                      </div>
                    )}
                    {message.hasMedia && message.mediaPath && (
                      <div className="message-media">
                        {message.type === 'image' && (
                          <img
                            src={`${API_BASE_URL}/${message.mediaPath}`}
                            alt="תמונה"
                            onClick={() => handleImageClick(message.mediaPath)}
                            onError={(e) => {
                              console.error('❌ Image failed to load:', `${API_BASE_URL}/${message.mediaPath}`);
                              // Show placeholder instead of hiding
                              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23333" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23888" font-size="12">לא נטען</text></svg>';
                              e.target.style.opacity = '0.5';
                              e.target.onclick = () => window.open(`${API_BASE_URL}/${message.mediaPath}`, '_blank');
                            }}
                            loading="lazy"
                          />
                        )}
                        {message.type === 'video' && (
                          <video controls>
                            <source src={`${API_BASE_URL}/${message.mediaPath}`} />
                          </video>
                        )}
                        {(message.type === 'audio' || message.type === 'ptt') && (
                          <div className={`audio-message ${playingAudio === message.id ? 'playing' : ''}`}>
                            <button
                              className="play-pause-btn"
                              onClick={() => handleAudioPlay(message.id, message.mediaPath)}
                            >
                              {playingAudio === message.id ? (
                                <svg viewBox="0 0 34 34">
                                  <path fill="currentColor" d="M9.5 8A1.5 1.5 0 0 0 8 9.5v15a1.5 1.5 0 0 0 1.5 1.5h3a1.5 1.5 0 0 0 1.5-1.5v-15A1.5 1.5 0 0 0 12.5 8h-3zm8 0A1.5 1.5 0 0 0 16 9.5v15a1.5 1.5 0 0 0 1.5 1.5h3a1.5 1.5 0 0 0 1.5-1.5v-15a1.5 1.5 0 0 0-1.5-1.5h-3z"/>
                                </svg>
                              ) : (
                                <svg viewBox="0 0 34 34">
                                  <path fill="currentColor" d="M8.5 8.7c0-1.7 1.2-2.4 2.6-1.5l14.4 8.3c1.4.8 1.4 2.2 0 3l-14.4 8.3c-1.4.8-2.6.2-2.6-1.5V8.7z"/>
                                </svg>
                              )}
                            </button>
                            <div className="audio-waveform">
                              {[...Array(40)].map((_, i) => {
                                const heights = [6, 8, 12, 16, 20, 18, 14, 10, 8, 12, 16, 20, 22, 18, 12, 8, 14, 18, 16, 12, 8, 10, 16, 20, 18, 14, 10, 12, 16, 14, 10, 8, 12, 16, 18, 14, 10, 8, 12, 10];
                                return (
                                  <div
                                    key={i}
                                    className="waveform-bar"
                                    style={{ height: `${heights[i]}px` }}
                                  ></div>
                                );
                              })}
                            </div>
                            <span className="audio-duration">0:00</span>
                          </div>
                        )}
                        {message.type === 'document' && (
                          <a 
                            href={`${API_BASE_URL}/${message.mediaPath}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="document-link"
                          >
                            <svg viewBox="0 0 24 24" height="40" width="40">
                              <path fill="currentColor" d="M7.75 3.25C6.55 3.25 5.5 4.3 5.5 5.5v13c0 1.2 1.05 2.25 2.25 2.25h8.5c1.2 0 2.25-1.05 2.25-2.25v-13c0-1.2-1.05-2.25-2.25-2.25h-8.5zm0 1.5h8.5c.45 0 .75.3.75.75v13c0 .45-.3.75-.75.75h-8.5c-.45 0-.75-.3-.75-.75v-13c0-.45.3-.75.75-.75z"></path>
                            </svg>
                            <span>מסמך</span>
                          </a>
                        )}
                      </div>
                    )}
                    {message.body && <p>{message.body}</p>}
                    <div className="message-footer">
                      {starredMessages.includes(message.id) && (
                        <svg viewBox="0 0 12 11" height="12" width="12" className="message-star">
                          <path fill="currentColor" d="M6 0l1.543 3.458L11 4.122 8.5 6.823l.592 3.477L6 8.615 2.908 10.3l.592-3.477L1 4.122l3.457-.664L6 0z"/>
                        </svg>
                      )}
                      <span className="message-time">
                        {formatTime(message.timestamp)}
                        {message.fromMe && (
                          <svg viewBox="0 0 16 15" height="11" width="16" className="message-check">
                            <path fill="currentColor" d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"></path>
                          </svg>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Message Action Buttons - בדיוק כמו וואטסאפ אמיתי */}
                  <div className="message-actions">
                    <button
                      className="msg-action-btn reply-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReply(message);
                      }}
                      title="השב"
                    >
                      <svg viewBox="0 0 16 16" height="16" width="16">
                        <path fill="currentColor" d="M8.5 2v4.5H2v1h6.5V12L14 7z"/>
                      </svg>
                    </button>
                    <button
                      className="msg-action-btn menu-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMessageRightClick(e, message);
                      }}
                      title="תפריט"
                    >
                      <svg viewBox="0 0 18 18" height="18" width="18">
                        <path fill="currentColor" d="M3.3,4.6L9,10.3l5.7-5.7l1.6,1.6L9,13.4L1.7,6.2L3.3,4.6z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Recording overlay */}
            {isRecording && (
              <div className="recording-overlay">
                <button className="cancel-recording" onClick={cancelRecording}>
                  <svg viewBox="0 0 24 24" height="24" width="24">
                    <path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path>
                  </svg>
                </button>
                <div className="recording-info">
                  <span className="recording-timer">{formatRecordingTime(recordingTimer)}</span>
                  <span className="recording-text">מקליט...</span>
                  <div className="recording-wave">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
                <button className="send-recording" onClick={stopRecording}>
                  <svg viewBox="0 0 24 24" height="24" width="24">
                    <path fill="currentColor" d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"></path>
                  </svg>
                </button>
              </div>
            )}

            {/* Emoji picker */}
            {showEmojiPicker && (
              <div className="emoji-picker">
                <div className="emoji-grid">
                  {popularEmojis.map(emoji => (
                    <button 
                      key={emoji}
                      onClick={() => handleEmojiClick(emoji)}
                      className="emoji-btn"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Attach menu */}
            {showAttachMenu && (
              <div className="attach-menu">
                <button onClick={() => {
                  fileInputRef.current.accept = 'image/*';
                  fileInputRef.current.click();
                }}>
                  <svg viewBox="0 0 53 53" height="53" width="53">
                    <path fill="#BFC9CA" d="M26.5 6.5c11 0 20 9 20 20s-9 20-20 20-20-9-20-20 9-20 20-20z"></path>
                    <path fill="#F3F3F3" d="M20 23.5v-6c0-.8.7-1.5 1.5-1.5h10c.8 0 1.5.7 1.5 1.5v10c0 .8-.7 1.5-1.5 1.5H28v6c0 .8-.7 1.5-1.5 1.5h-10c-.8 0-1.5-.7-1.5-1.5v-10c0-.8.7-1.5 1.5-1.5H20zm11.5-6h-10v7.5H29l-7.5-7.5zm-5 9v6h-10v-7.5H24l-7.5 7.5z"></path>
                  </svg>
                  <span>תמונות וסרטונים</span>
                </button>
                <button onClick={() => {
                  fileInputRef.current.accept = '.pdf,.doc,.docx,.txt';
                  fileInputRef.current.click();
                }}>
                  <svg viewBox="0 0 53 53" height="53" width="53">
                    <path fill="#5157AE" d="M26.5 6.5c11 0 20 9 20 20s-9 20-20 20-20-9-20-20 9-20 20-20z"></path>
                    <path fill="#F3F3F3" d="M29.5 17.5h-6c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2v-10l-6-6zm-6 0v14h10v-8h-4c-1.1 0-2-.9-2-2v-4h-4zm6 1.5l3.5 3.5h-3.5v-3.5z"></path>
                  </svg>
                  <span>מסמך</span>
                </button>
                <button onClick={() => alert('תכונה בפיתוח')}>
                  <svg viewBox="0 0 53 53" height="53" width="53">
                    <path fill="#FF7043" d="M26.5 6.5c11 0 20 9 20 20s-9 20-20 20-20-9-20-20 9-20 20-20z"></path>
                    <path fill="#F3F3F3" d="M26.5 16c-5.8 0-10.5 4.7-10.5 10.5S20.7 37 26.5 37 37 32.3 37 26.5 32.3 16 26.5 16zm0 19c-4.7 0-8.5-3.8-8.5-8.5s3.8-8.5 8.5-8.5 8.5 3.8 8.5 8.5-3.8 8.5-8.5 8.5zm0-15c-3.6 0-6.5 2.9-6.5 6.5s2.9 6.5 6.5 6.5 6.5-2.9 6.5-6.5-2.9-6.5-6.5-6.5zm0 11c-2.5 0-4.5-2-4.5-4.5s2-4.5 4.5-4.5 4.5 2 4.5 4.5-2 4.5-4.5 4.5z"></path>
                  </svg>
                  <span>איש קשר</span>
                </button>
              </div>
            )}

            <div className="message-input-container">
              <button 
                className="icon-btn" 
                title="אימוג'י"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              >
                <svg viewBox="0 0 24 24" height="24" width="24">
                  <path fill="currentColor" d="M9.153 11.603c.795 0 1.439-.879 1.439-1.962s-.644-1.962-1.439-1.962-1.439.879-1.439 1.962.644 1.962 1.439 1.962zm-3.204 1.362c-.026-.307-.131 5.218 6.063 5.551 6.066-.25 6.066-5.551 6.066-5.551-6.078 1.416-12.129 0-12.129 0zm11.363 1.108s-.669 1.959-5.051 1.959c-4.27 0-5.064-1.959-5.064-1.959s.283.005.665.037c.07.492.246.953.481 1.337.326.531.779.951 1.304 1.212.2.1.38.164.492.174.046.005.091.009.137.013.066.004.132.007.198.007.423 0 .729-.044.729-.044s.256.033.588.033c.314 0 .555-.027.555-.027s.304.044.729.044c.066 0 .132-.003.198-.007.046-.004.091-.008.137-.013.112-.01.293-.074.492-.174.525-.261.979-.681 1.304-1.212.235-.384.411-.845.481-1.337.382-.032.665-.037.665-.037z"></path>
                  <path fill="currentColor" d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0m0 1c6.071 0 11 4.929 11 11s-4.929 11-11 11S1 18.071 1 12 5.929 1 12 1zm5.247 6.879c-.795 0-1.439.879-1.439 1.962s.644 1.962 1.439 1.962 1.439-.879 1.439-1.962-.644-1.962-1.439-1.962z"></path>
                </svg>
              </button>
              <button 
                className="icon-btn" 
                onClick={() => setShowAttachMenu(!showAttachMenu)} 
                title="קובץ"
              >
                <svg viewBox="0 0 24 24" height="24" width="24">
                  <path fill="currentColor" d="M1.816 15.556v.002c0 1.502.584 2.912 1.646 3.972s2.472 1.647 3.974 1.647a5.58 5.58 0 0 0 3.972-1.645l9.547-9.548c.769-.768 1.147-1.767 1.058-2.817-.079-.968-.548-1.927-1.319-2.698-1.594-1.592-4.068-1.711-5.517-.262l-7.916 7.915c-.881.881-.792 2.25.214 3.261.959.958 2.423 1.053 3.263.215l5.511-5.512c.28-.28.267-.722.053-.936l-.244-.244c-.191-.191-.567-.349-.957.04l-5.506 5.506c-.18.18-.635.127-.976-.214-.098-.097-.576-.613-.213-.973l7.915-7.917c.818-.817 2.267-.699 3.23.262.5.501.802 1.1.849 1.685.051.573-.156 1.111-.589 1.543l-9.547 9.549a3.97 3.97 0 0 1-2.829 1.171 3.975 3.975 0 0 1-2.83-1.173 3.973 3.973 0 0 1-1.172-2.828c0-1.071.415-2.076 1.172-2.83l7.209-7.211c.157-.157.264-.579.028-.814L11.5 4.36a.572.572 0 0 0-.834.018l-7.205 7.207a5.577 5.577 0 0 0-1.645 3.971z"></path>
                </svg>
              </button>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <form onSubmit={handleSendMessage} className="message-input">
                <input
                  type="text"
                  placeholder="הקלד הודעה"
                  value={messageText}
                  onChange={handleTyping}
                />
                {messageText.trim() ? (
                  <button type="submit" className="send-btn">
                    <svg viewBox="0 0 24 24" height="24" width="24">
                      <path fill="currentColor" d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"></path>
                    </svg>
                  </button>
                ) : (
                  <button 
                    type="button"
                    className={`icon-btn ${isRecording ? 'recording' : ''}`} 
                    onMouseDown={startRecording}
                    onTouchStart={startRecording}
                    onMouseUp={stopRecording}
                    onTouchEnd={stopRecording}
                    onMouseLeave={stopRecording}
                    title="החזק להקלטה"
                  >
                    <svg viewBox="0 0 24 24" height="24" width="24">
                      <path fill="currentColor" d="M11.999 14.942c2.001 0 3.531-1.53 3.531-3.531V4.35c0-2.001-1.53-3.531-3.531-3.531S8.469 2.35 8.469 4.35v7.061c0 2.001 1.53 3.531 3.53 3.531zm6.238-3.53c0 3.531-2.942 6.002-6.237 6.002s-6.237-2.471-6.237-6.002H3.761c0 4.001 3.178 7.297 7.061 7.885v3.884h2.354v-3.884c3.884-.588 7.061-3.884 7.061-7.885h-2z"></path>
                    </svg>
                  </button>
                )}
              </form>
            </div>
            
          </>
     ) : (
          <div className="welcome-screen">
            <div className="welcome-content">
              <img src="https://static.whatsapp.net/rsrc.php/v3/y6/r/wa669aeJeom.png" alt="WhatsApp" />
              <h1>WhatsApp Web</h1>
              <p>שלח וקבל הודעות בלי להשאיר את הטלפון שלך מחובר לאינטרנט</p>
              <p>השתמש ב-WhatsApp במחשב שלך:</p>
              <ol>
                <li>פתח את WhatsApp בטלפון שלך</li>
                <li>הקש על הגדרות &#62; מכשירים מקושרים</li>
                <li>הקש על קשר מכשיר</li>
                <li>כוון את הטלפון אל המסך כדי לסרוק את הקוד</li>
              </ol>
              <div className="status-badge">
                <svg viewBox="0 0 24 24" height="20" width="20">
                  <path fill="#00a884" d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"></path>
                </svg>
                <span>מחובר בהצלחה!</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {showUserPanel && selectedUser && (
        <UserDetailsPanel
          selectedUser={selectedUser}
          onClose={() => setShowUserPanel(false)}
          socket={socket}
        />
      )}

      {showPersonalDashboard && (
        <PersonalDashboard
          onClose={() => setShowPersonalDashboard(false)}
          socket={socket}
          chats={chats}
        />
      )}

      {showSavedMessages && (
        <SavedMessages
          onClose={() => setShowSavedMessages(false)}
          selectedChat={selectedChat}
          socket={socket}
        />
      )}

      {/* חלון צף למובייל */}
      {showMobileUserModal && selectedChat && (
        <div className="mobile-user-modal-overlay" onClick={() => setShowMobileUserModal(false)}>
          <div className="mobile-user-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-user-modal-header">
              <h3>{selectedChat.name}</h3>
              <button className="close-modal-btn" onClick={() => setShowMobileUserModal(false)}>
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path fill="currentColor" d="M19.6 17.8L13.3 11.5l6.3-6.3c.5-.5.5-1.3 0-1.8-.5-.5-1.3-.5-1.8 0l-6.3 6.3-6.3-6.3c-.5-.5-1.3-.5-1.8 0-.5.5-.5 1.3 0 1.8l6.3 6.3-6.3 6.3c-.5.5-.5 1.3 0 1.8.3.3.6.4.9.4s.6-.1.9-.4l6.3-6.3 6.3 6.3c.3.3.6.4.9.4s.6-.1.9-.4c.5-.5.5-1.3 0-1.8z"/>
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="mobile-modal-tabs">
              <button
                className={`mobile-tab ${mobileModalTab === 'info' ? 'active' : ''}`}
                onClick={() => setMobileModalTab('info')}
              >
                מידע
              </button>
              <button
                className={`mobile-tab ${mobileModalTab === 'progress' ? 'active' : ''}`}
                onClick={() => setMobileModalTab('progress')}
              >
                התקדמות
              </button>
              {selectedChat.inFirebase === 1 && (
                <button
                  className={`mobile-tab ${mobileModalTab === 'journals' ? 'active' : ''}`}
                  onClick={() => setMobileModalTab('journals')}
                >
                  יומנים
                </button>
              )}
              <button
                className={`mobile-tab ${mobileModalTab === 'actions' ? 'active' : ''}`}
                onClick={() => setMobileModalTab('actions')}
              >
                פעולות
              </button>
            </div>

            <div className="mobile-user-modal-body">
              {/* Tab: מידע */}
              {mobileModalTab === 'info' && (
                <div className="mobile-tab-content">
                  <div className="user-info-item">
                    <strong>📱 טלפון:</strong>
                    <span>{selectedChat.id.split('@')[0]}</span>
                  </div>
                  {selectedChat.email && (
                    <div className="user-info-item">
                      <strong>📧 אימייל:</strong>
                      <span>{selectedChat.email}</span>
                    </div>
                  )}
                  {selectedChat.fullName && (
                    <div className="user-info-item">
                      <strong>👤 שם מלא:</strong>
                      <span>{selectedChat.fullName}</span>
                    </div>
                  )}
                  <div className="user-info-item">
                    <strong>📊 סטטוס:</strong>
                    <span style={{ color: selectedChat.inFirebase === 1 ? '#00e676' : '#8696a0' }}>
                      {selectedChat.inFirebase === 1 ? 'תלמיד פעיל' : 'לא רשום'}
                    </span>
                  </div>
                </div>
              )}

              {/* Tab: התקדמות */}
              {mobileModalTab === 'progress' && selectedChat.inFirebase === 1 && (
                <div className="mobile-tab-content">
                  <div className="progress-stage-card">
                    <div className="stage-icon-large">
                      {progressStages[selectedChat.progressStage || 'watching_training']?.icon}
                    </div>
                    <h4>שלב נוכחי</h4>
                    <p className="stage-name">
                      {progressStages[selectedChat.progressStage || 'watching_training']?.label}
                    </p>
                  </div>

                  <div className="all-stages">
                    <h5>כל השלבים:</h5>
                    {Object.entries(progressStages).map(([stageId, stage]) => (
                      <div
                        key={stageId}
                        className={`stage-item ${selectedChat.progressStage === stageId ? 'current' : ''}`}
                      >
                        <span className="stage-icon">{stage.icon}</span>
                        <span className="stage-label">{stage.label}</span>
                        {selectedChat.progressStage === stageId && (
                          <span className="current-badge">נוכחי</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab: יומני מסחר */}
              {mobileModalTab === 'journals' && selectedChat.inFirebase === 1 && (
                <div className="mobile-tab-content">
                  {userJournals.length === 0 ? (
                    <div className="no-data-mobile">
                      <div className="no-data-icon">📓</div>
                      <p>אין יומני מסחר</p>
                      <small style={{ color: '#667781', marginTop: '8px' }}>משתמש זה עדיין לא יצר יומני מסחר</small>
                    </div>
                  ) : (
                    userJournals.map((journal, index) => (
                      <div key={journal.id || index} className="journal-card-mobile">
                        <div className="journal-header-mobile">
                          <div className="journal-title-mobile">
                            <span className="journal-icon">📓</span>
                            <strong>{journal.name}</strong>
                          </div>
                          <span className="journal-currency-mobile">{journal.currency || 'USD'}</span>
                        </div>

                        {journal.description && (
                          <p className="journal-description-mobile">{journal.description}</p>
                        )}

                        <div className="journal-stats-mobile">
                          <div className="journal-stat-mobile">
                            <span className="stat-label">יתרה התחלתית:</span>
                            <span className="stat-value">{journal.initialBalance?.toLocaleString() || 0}</span>
                          </div>
                          <div className="journal-stat-mobile">
                            <span className="stat-label">גודל תיק:</span>
                            <span className="stat-value">{journal.portfolioSize?.toLocaleString() || 0}</span>
                          </div>
                          <div className="journal-stat-mobile">
                            <span className="stat-label">סוג סיכון:</span>
                            <span className="stat-value">{journal.riskType === 'fixed' ? 'קבוע' : 'אחוז'}</span>
                          </div>
                          <div className="journal-stat-mobile">
                            <span className="stat-label">ערך סיכון:</span>
                            <span className="stat-value">{journal.riskValue}</span>
                          </div>
                        </div>

                        <div className="journal-dates-mobile">
                          <span>נוצר: {journal.createdAt ? new Date(journal.createdAt).toLocaleDateString('he-IL') : 'לא ידוע'}</span>
                          <span>עודכן: {journal.updatedAt ? new Date(journal.updatedAt).toLocaleDateString('he-IL') : 'לא ידוע'}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Tab: פעולות */}
              {mobileModalTab === 'actions' && selectedChat.inFirebase === 1 && (
                <div className="mobile-tab-content">
                  <button
                    className="action-btn-mobile reset"
                    onClick={() => {
                      setShowMobileUserModal(false);
                      setSelectedUser(selectedChat);
                      setShowUserPanel(true);
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    פרטים מלאים
                  </button>

                  <button
                    className="action-btn-mobile reset"
                    onClick={() => {
                      const resetLink = 'https://darkcrypto.io/reset-password?email=' + encodeURIComponent(selectedChat.email || '');
                      const message = '🔐 קישור לאיפוס סיסמה\n\nשלום ' + selectedChat.name + ',\n\nלהלן קישור לאיפוס סיסמה שלך בפלטפורמה:\n' + resetLink + '\n\n🔹 הקישור בתוקף ל-24 שעות\n💡 לכל שאלה או בעיה, אנחנו כאן לעזור!';
                      socket.emit('send_message', { chatId: selectedChat.id, body: message });
                      setShowMobileUserModal(false);
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                    </svg>
                    שלח איפוס סיסמה
                  </button>

                  <button
                    className="action-btn-mobile grant"
                    onClick={() => {
                      const loginLink = 'https://darkcrypto.io/login';
                      const message = '🎉 קיבלת גישה לפלטפורמה!\n\nשלום ' + selectedChat.name + ',\n\nמזל טוב! קיבלת גישה לפלטפורמת ההכשרה שלנו.\n\n🔗 קישור להתחברות:\n' + loginLink + '\n\n📧 השתמש באימייל שלך כדי להתחבר\n🔐 אם שכחת את הסיסמה, תוכל לאפס אותה\n\n💡 בהצלחה בהכשרה!';
                      socket.emit('send_message', { chatId: selectedChat.id, body: message });
                      socket.emit('grant_platform_access', {
                        userId: selectedChat.id.split('@')[0],
                        grantedBy: 'admin'
                      });
                      setShowMobileUserModal(false);
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                    </svg>
                    מתן גישה לפלטפורמה
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;
